import { computed, inject, Injectable, signal } from '@angular/core';
import { filter, map, Observable, take, tap } from 'rxjs';
import { IWorkItem } from '@dxp/core/models';
import { WORKITEMS_STORE } from '@dxp/core/stores';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { VoiceApiService } from '@dxp/core/api';
import { SipService } from '../sip/sip.service';

@Injectable({
    providedIn: 'root',
})
export class MonitoringService {
    readonly #voiceApiService = inject(VoiceApiService);
    readonly #sipService = inject(SipService);
    readonly #workItemsStore = inject(WORKITEMS_STORE);
    readonly #router = inject(Router);

    /**
     * Hold the state for the disabled button
     */
    #disabledButton = signal(false);

    /**
     * @Important Flag to control whether or not we can display a supervision icon to the agent
     * @Plan to implement global config to control this setting
     */
    displaySupervisionIcon = signal<boolean>(false);

    /**
     * Hold the state for the monitoring
     */
    #monitoredConversations = computed<Record<string, IWorkItem>>(() => {
        return this.#workItemsStore
            .workItems()
            .filter(
                workItem =>
                    workItem.channelIdentifier === 'Monitor' &&
                    workItem.conversations.length > 0,
            )
            .reduce(
                (accumulator, current) =>
                    (accumulator = {
                        ...accumulator,
                        ...{
                            [`${current.primaryConversationId}`]: current,
                        },
                    }),
                {},
            );
    });

    /**
     * Public API to read monitored conversations
     */
    activeMonitoredConversations = computed(() =>
        this.#monitoredConversations(),
    );

    /**
     * Check if any tasks are being actively monitored
     */
    monitoringActive = computed<boolean>(
        () => Object.entries(this.#monitoredConversations()).length > 0,
    );

    /**
     * Public API for the disabled button
     */
    buttonIsDisabled = computed<boolean>(() => this.#disabledButton());

    /**
     * Get the WorkItem Id for the Active Monitored Task
     */
    activeMonitoredWorkItemId = computed(() => {
        const key = Object.keys(this.#monitoredConversations())[0];
        const activeMonitor = this.#monitoredConversations()[key];

        return activeMonitor.workItemId;
    });

    /**
     * Emits once
     * When the the monitored conversation's WorkItem is available
     */
    monitoredConversation$ = toObservable(
        this.#workItemsStore.activeMonitoredTask,
    ).pipe(
        takeUntilDestroyed(),
        filter(workItems => (workItems ?? []).length > 0),
        map(workItem => workItem![0]),
    );

    /**
     *
     */
    #disableButton(): void {
        this.#disabledButton.set(true);
    }

    /**
     *
     */
    enableButton(): void {
        this.#disabledButton.set(false);
    }

    /**
     * Setup the data before we start the monitoring task
     */
    initiateMonitoring(conversationId: string): Observable<void> {
        this.#disableButton();
        this.#workItemsStore.updateActiveConversationId(conversationId);
        return this.startMonitoring(conversationId);
    }

    /**
     *
     * @param workItemId
     * @param conversationId
     */
    navigateToMonitoringScreen(
        workItemId: string,
        conversationId: string,
    ): void {
        /**
         * Set the active workItem Id
         */
        this.#workItemsStore.updateActiveWorkId(workItemId);

        /**
         * Navigate to the monitoring screen
         */
        this.#router.navigate([
            `/comms/voice/monitor/${workItemId}/${conversationId}`,
        ]);
    }

    /**
     * Start Monitoring
     * @information Only Emits Once - No need to unsubscribe
     */
    startMonitoring(conversationId: string): Observable<void> {
        this.#sipService.setAutoAnswer();
        return this.#voiceApiService
            .startVoiceMontior(conversationId)
            .pipe(take(1));
    }

    /**
     * Stop Monitoring
     * @information Only Emits Once - No need to unsubscribe
     */
    stopMonitoring(callId: number, conversationId: string): Observable<void> {
        this.#sipService.hangUp();
        return this.#voiceApiService
            .stopVoiceMontior({
                conversationId: conversationId,
                callId: callId,
            })
            .pipe(
                tap(() => this.enableButton()),
                take(1),
            );
    }
}
